// NEC edition packs — every parameter and section number the traditional
// engines need, per code edition. Reports and UI must cite through
// `sectionMap`, never literal section strings: 2020→2023 moved the dwelling
// lighting sections (220.12 → 220.41, Table 220.42 → Table 220.45), and 2026
// relocated all load calculations into Article 120.
//
// Verification state is tracked per pack. 2023 and 2026 parameters were
// verified against the primary NFPA text on 2026-08-31, 2020 on 2026-09-06
// and 2017 on 2026-10-22 (see docs/specs/traditional-calcs.md §3.2/§6.5 — the NEC text itself is
// copyrighted and never enters this repo). State amendment overlays are
// checked before a state ships as a default: Massachusetts (527 CMR 12.00)
// closed 2026-09-06, Maine (Rule Chapter 120) closed 2026-09-13, Chicago
// (Municipal Code Title 14E, for the 2017 pack) closed 2026-10-22. Each pack's
// `verification` string records what was read and what was found.

export type EditionKey = '2017' | '2020' | '2023' | '2026';

export type CanonicalRef =
  | 'article'
  | 'existingDwelling'
  | 'optionalDwelling'
  | 'multifamilyOptional'
  | 'twoDwellingUnits'
  | 'standardMethodPart'
  | 'dwellingLightingUnitLoad'
  /** What 220.83/120.83 itself cites for its 3 (or 2) VA/ft² lighting item —
   * distinct from dwellingLightingUnitLoad, which serves the standard-method
   * plans. The 220.83 worksheet must cite what 220.83 cites. */
  | 'optional83LightingRef'
  /** 210.11(C)(1) and (C)(2) — the small-appliance and laundry branch-circuit
   * provisions the optional methods cite for their 1,500 VA items. */
  | 'branchCircuitProvisions'
  | 'lightingDemandTable'
  | 'smallApplianceLaundry'
  | 'applianceDemand'
  | 'dryers'
  | 'cooking'
  | 'evse'
  | 'noncoincident'
  /** The branch-circuit / feeder / service conductor sizing sections the
   * measured report names as still applying. Null where the edition's numbers
   * have not been read from the primary text — the sentence then renders
   * without citations rather than carrying an inferred one. */
  | 'conductorSizing'
  | 'determiningExisting';

export type EvseRule =
  | 'none' // pre-2023: no dedicated EVSE section; nameplate as entered
  | 'max7200' // 2023 (220.57): larger of 7,200 VA or nameplate
  | 'nameplateElse7200'; // 2026 (120.57): nameplate; 7,200 VA only if unavailable

export interface New83Percentages {
  newEvse: number;
  newCentralResistanceHeat: number;
  otherNewLoads: number;
}

export interface EditionPack {
  edition: EditionKey;
  /** Feeder/service general lighting for dwellings. Branch-circuit count keeps 3 VA/ft² in 2026 (120.13). */
  lightingVaPerSqFt: number;
  /** 220.82(B) / 120.82(B) first tier, kVA at 100%. */
  optional82TierKVA: number;
  /** 220.83 / 120.83 first tier, kVA at 100%. */
  optional83TierKVA: number;
  evseRule: EvseRule;
  /** EVSE excluded from the 75% appliance demand factor (220.53 / 120.53), 2023+. */
  evseExcludedFromApplianceDF: boolean;
  /** 2020/2023 have the (A)/(B) fork; 2026 has the single Table 120.83. */
  existing83Shape: 'AB' | 'unified2026';
  /** unified2026 only: per-category percentages for new loads (as fractions). */
  new83Percentages?: New83Percentages;
  /** 2026 only: 120.82(D) adds EVSE at 100% outside the 40% pool. */
  optional82EvseAt100: boolean;
  /** The 220.87 30-day Exception may not be used where the service has a
   * renewable energy system or any form of peak load shaving. 2020 onward;
   * the 2017 Exception carries no such restriction. */
  exceptionBarsRenewables: boolean;
  sectionMap: Record<CanonicalRef, string | null>;
  /** True until every parameter and section number in this pack has been
   * verified against the primary code text. A provisional pack's
   * `verification` string is surfaced as a calculation warning so it reaches
   * the results screen and the report's disclosure block. */
  provisional: boolean;
  /** Human-readable provenance shown in methodology notes. */
  verification: string;
}

export const EDITION_PACKS: Record<EditionKey, EditionPack> = {
  '2017': {
    edition: '2017',
    lightingVaPerSqFt: 3,
    optional82TierKVA: 10,
    optional83TierKVA: 8,
    evseRule: 'none',
    evseExcludedFromApplianceDF: false,
    existing83Shape: 'AB',
    optional82EvseAt100: false,
    // Verified from the 2017 text (NFPA free access, p. 70-75, 2026-10-22):
    // the Exception to 220.87(1) runs from the 30-day recording through "other
    // loads that may be periodic in nature due to seasonal or similar
    // conditions" and stops. The renewable-energy and peak-load-shaving bar
    // arrived in 2020.
    exceptionBarsRenewables: false,
    sectionMap: {
      article: 'Article 220',
      existingDwelling: '220.83',
      optionalDwelling: '220.82',
      multifamilyOptional: '220.84',
      twoDwellingUnits: '220.85',
      standardMethodPart: 'Article 220 Part III',
      dwellingLightingUnitLoad: '220.12',
      // Verified from the 2017 text (p. 70-76, 2026-10-22): 220.83(A)(1) and
      // (B)(1) read "at 33 volt-amperes/m2 or 3 volt-amperes/ft2 as
      // determined by 220.12", the same as 2020.
      optional83LightingRef: '220.12',
      branchCircuitProvisions: '210.11(C)(1) and (C)(2)',
      lightingDemandTable: 'Table 220.42',
      smallApplianceLaundry: '220.52',
      applianceDemand: '220.53',
      dryers: '220.54',
      cooking: '220.55',
      evse: null,
      noncoincident: '220.60',
      conductorSizing: '210.19, 215.2, and 230.42',
      determiningExisting: '220.87',
    },
    provisional: false,
    verification:
      "Verified against NFPA 70-2017 Article 220, pp. 70-75 to 70-77 (2026-10-22). Parameters: 220.83 first 8 kVA at 100% with the remainder at 40% in both (A) and (B); the (B) table counts air conditioning, central electric space heating and fewer than four separately controlled space-heating units at 100%, the larger of air conditioning or space heating but not both; 3 VA/ft2 general lighting citing 220.12; 1,500 VA small-appliance and laundry circuits citing 210.11(C)(1) and (C)(2); applies to a dwelling on a 3-wire 120/240 V or 208Y/120 V service. Identical to the 2020 pack on every value the 220.83 engine uses. 220.84, 220.85 and 220.87 read as printed. 220.87 differs from 2020 in one place: its 30-day Exception has no bar on services with renewable energy or peak load shaving. 220.82(B) was not in the pages read; its 10 kVA tier is carried from 2020 and the engine does not compute 220.82. No EVSE load-calculation section, so evseRule is none. Chicago overlay closed 2026-10-22: Municipal Code of Chicago Title 14E (amlegal, current through 2026-06-17) adopts NFPA 70-2017 by reference (14E-1-010), and 14E-2-220 adopts Article 220 by reference without modification. Of the other sections the reports cite, Chicago revises 210.19(A) and 215.2(A)(1)(b) (conductor sizing, still the same section numbers) and adds an informational note ahead of 210.11(A); 210.11(C), 230.42, 230.90 and 240.4 are not amended.",
  },
  '2020': {
    edition: '2020',
    lightingVaPerSqFt: 3,
    optional82TierKVA: 10,
    optional83TierKVA: 8,
    evseRule: 'none',
    evseExcludedFromApplianceDF: false,
    existing83Shape: 'AB',
    optional82EvseAt100: false,
    exceptionBarsRenewables: true,
    sectionMap: {
      article: 'Article 220',
      existingDwelling: '220.83',
      optionalDwelling: '220.82',
      multifamilyOptional: '220.84',
      twoDwellingUnits: '220.85',
      standardMethodPart: 'Article 220 Part III',
      dwellingLightingUnitLoad: '220.12',
      // Verified from the 2020 text (NFPA free access, p. 70-80, 2026-09-06):
      // 220.83(A)(1) and (B)(1) both read "at 33 volt-amperes/m2 or 3
      // volt-amperes/ft2 as determined by 220.12". The 2023 edition moved that
      // cross-reference to 220.42; the worksheet cites what the section cites,
      // so the two editions cite different numbers on the same line.
      optional83LightingRef: '220.12',
      branchCircuitProvisions: '210.11(C)(1) and (C)(2)',
      lightingDemandTable: 'Table 220.42',
      smallApplianceLaundry: '220.52',
      applianceDemand: '220.53',
      dryers: '220.54',
      cooking: '220.55',
      evse: null,
      noncoincident: '220.60',
      conductorSizing: '210.19, 215.2, and 230.42',
      determiningExisting: '220.87',
    },
    provisional: false,
    verification:
      'Verified against NFPA 70-2020 Article 220, pp. 70-74 to 70-81 (2026-09-06). Parameters: 220.83 first 8 kVA at 100% with the remainder at 40% in both (A) and (B); (B) counts the larger of air conditioning or space heating at 100%; 3 VA/ft2 general lighting; 220.82 first 10 kVA at 100%; Table 220.42 lighting demand factors; and Article 220 has no EVSE load-calculation section, which is why evseRule is none. Citations: 220.52, 220.53, 220.54, 220.55, 220.56, 220.60, 220.83, 220.84, 220.85, 220.87 all read as printed, and 220.83 items (1) cite 220.12 where 2023 cites 220.42. The conductor-sizing triple (210.19, 215.2, 230.42) is carried over from the verified 2023 pack: those sections live in Articles 210, 215 and 230, which were not read in the 2020 book. It is a citation on a sentence about branch-circuit and feeder sizing, outside the load calculation, so it does not hold the pack provisional.',
  },
  '2023': {
    edition: '2023',
    lightingVaPerSqFt: 3,
    optional82TierKVA: 10,
    optional83TierKVA: 8,
    evseRule: 'max7200',
    evseExcludedFromApplianceDF: true,
    existing83Shape: 'AB',
    optional82EvseAt100: false,
    exceptionBarsRenewables: true,
    sectionMap: {
      article: 'Article 220',
      existingDwelling: '220.83',
      optionalDwelling: '220.82',
      multifamilyOptional: '220.84',
      twoDwellingUnits: '220.85',
      standardMethodPart: 'Article 220 Part III',
      dwellingLightingUnitLoad: '220.41',
      // Verbatim from the 2023 220.83 text Jake supplied: item (1) reads "as
      // determined by 220.42", and items (2) cite 210.11(C)(1) and (C)(2).
      // The worksheet cites what the section itself cites.
      optional83LightingRef: '220.42',
      branchCircuitProvisions: '210.11(C)(1) and (C)(2)',
      lightingDemandTable: 'Table 220.45',
      smallApplianceLaundry: '220.52',
      applianceDemand: '220.53',
      dryers: '220.54',
      cooking: '220.55',
      evse: '220.57',
      noncoincident: '220.60',
      conductorSizing: '210.19, 215.2, and 230.42',
      determiningExisting: '220.87',
    },
    provisional: false,
    verification: 'Verified against NFPA 70-2023 Article 220 (2026-08-31).',
  },
  '2026': {
    edition: '2026',
    lightingVaPerSqFt: 2,
    optional82TierKVA: 8,
    optional83TierKVA: 8,
    evseRule: 'nameplateElse7200',
    evseExcludedFromApplianceDF: true,
    existing83Shape: 'unified2026',
    new83Percentages: {
      newEvse: 0.8,
      newCentralResistanceHeat: 0.8,
      otherNewLoads: 0.5,
    },
    optional82EvseAt100: true,
    exceptionBarsRenewables: true,
    sectionMap: {
      article: 'Article 120',
      existingDwelling: '120.83',
      optionalDwelling: '120.82',
      // Verified against the primary Article 120 text (NFPA 70-2026
      // screenshots, pp. 70-83 to 70-86, 2026-08-31): "120.84 Multifamily
      // Dwelling" and the "Part III. Feeder and Service Load Calculations"
      // heading at 120.40 — not inferred from the renumbering pattern.
      multifamilyOptional: '120.84',
      // Verified against the primary Article 120 text (p. 70-85 screenshot,
      // 2026-08-31): "120.85 Two Dwelling Units".
      twoDwellingUnits: '120.85',
      standardMethodPart: 'Article 120 Part III',
      dwellingLightingUnitLoad: '120.41',
      // Null because the section cites nothing, not because it is unread.
      // Verified from the 2026 text (NFPA free access, p. 70-85, 2026-09-06):
      // 120.83 item (1) reads "General lighting and general-use receptacles at
      // 22 volt-amperes/m2 or 2 volt-amperes/ft2" and stops there — no
      // cross-reference, unlike 220.83 in 2020 (220.12) and 2023 (220.42). The
      // worksheet lighting line renders uncited under this edition because the
      // section itself is uncited.
      optional83LightingRef: null,
      // Verified from the same pages: 120.83 item (2) cites 210.11(C)(1)
      // and (C)(2) — Article 210 kept its number through the 2026 reorg.
      branchCircuitProvisions: '210.11(C)(1) and (C)(2)',
      lightingDemandTable: 'Table 120.45',
      smallApplianceLaundry: '120.52',
      applianceDemand: '120.53',
      dryers: '120.54',
      cooking: '120.55',
      evse: '120.57',
      // Verified from the primary 2026 text, not the renumbering pattern:
      // 120.6's own wording was read (it now recognises loads "prevented from
      // being in use simultaneously by listed equipment").
      noncoincident: '120.6',
      // Null because the 2026 text has no counterpart to cite, not because it
      // is unread: Jake read Articles 215 and 230 in the 2026 edition on
      // 2026-09-02 and found no 215.2 and no 230.42 — the minimum-size rules
      // went into Article 120 with the rest of the load calculations.
      // Corroborated on p. 70-112, where 225.39's informational note now reads
      // "See Article 120 for calculating the load supplied by the
      // disconnecting means." The measured report's closing sentence therefore
      // names the branch-circuit, feeder and service provisions in words under
      // a 2026 pack instead of printing section numbers that no longer exist.
      conductorSizing: null,
      // Verified from the primary 2026 text: the section was read closely
      // enough to record what changed inside it — Condition (2)'s new
      // removed-load subtraction and its pointer to Parts III/IV, and the
      // 15-minute interval definition staying inside the 30-day Exception
      // (docs/specs/traditional-calcs.md §6.7). Not inferred.
      determiningExisting: '120.87',
    },
    provisional: false,
    verification:
      'Verified against NFPA 70-2026 Article 120, pp. 70-78 to 70-87 (2026-08-31), and Table 120.83 re-read from p. 70-85 (2026-09-06): first 8 kVA of existing and new load at 100%, remainder of existing at 40%, new EVSE at 80%, new central electric resistance space heating at 80%, all other new loads at 50%. 120.82 first 8 kVA at 100%, 2 VA/ft2 lighting, EVSE at 100% per 120.57. Massachusetts overlay closed 2026-09-06: 527 CMR 12.00 (effective 2026-04-24) amends Article 120 in exactly one place, an informational note at 120.40 about Stretch Energy Code pre-wiring in NEW homes, which is explanatory under 90.5(C) and not a load-calculation rule. No Massachusetts amendment touches 120.82, 120.83, 120.87 or any demand factor. Maine overlay closed 2026-09-13: Electricians\' Examining Board Rule Chapter 120, Electrical Installation Standards (02-318 Ch. 120, repealed and replaced by Board vote 2026-05-29; installations commencing on or after 2026-07-01 must comply with the 2026 NEC), read in full from the adopted text on maine.gov. It lists 14 amendments and exclusions: 90.4(D), the Article 100 definition of Dormitory, 110.16 (the 2023 text substituted), 210.8(A)(7), 210.8(B)(2), 210.8(F), 230.4(E), 334.10(3), 334.12(2) not adopted, 338.12(B), 400.12(4), 406.9(C)(1), 625.43(D)(1)(2) not adopted, and 702.4(A)(2)(a). None amends Article 120. The only mention of Article 120 is the cross-reference inside 702.4(A)(2)(a), which the prior rule made to Article 220 Parts I through IV and this one updates to Article 120 Parts I through IV; it is a standby-system rule, not a load-calculation rule. No Maine amendment touches 120.82, 120.83, 120.87, Table 120.83 or any demand factor.',
  },
};

export const DEFAULT_EDITION: EditionKey = '2023';

// Only verified anchors ship as state defaults; everything else falls back to
// DEFAULT_EDITION and the user can override per project (the AHJ is the final
// word, not the state). MA: 2026 NEC effective 2026-04-24. ME: 2026 NEC for
// installations commencing on or after 2026-07-01 (Electricians' Examining
// Board Rule Chapter 120; overlay check recorded in the 2026 pack's
// verification string). CT: 2020 NEC under the 2022 State Building Code; the
// next state code cycle is delayed. Chicago enforces the 2017 NEC (Title 14E)
// but Illinois has no statewide code, so 2017 is chosen per project there,
// not defaulted by state.
export const STATE_EDITION_DEFAULTS: Record<string, EditionKey> = {
  MA: '2026',
  ME: '2026',
  CT: '2020',
};

export function getEditionPack(edition?: string | null): EditionPack {
  if (edition && edition in EDITION_PACKS) {
    return EDITION_PACKS[edition as EditionKey];
  }
  return EDITION_PACKS[DEFAULT_EDITION];
}

export function defaultEditionForState(state?: string | null): EditionKey {
  const key = (state || '').trim().toUpperCase();
  return STATE_EDITION_DEFAULTS[key] ?? DEFAULT_EDITION;
}

// `projects.code_edition` is NULL until the preparer chooses an edition (or
// the project is completed). Every reader resolves the same way — the stored
// value if there is one, otherwise the state default — so the row and the
// screen cannot disagree. The default is never written into the row: doing
// that was the bug, because a project promoted to the cloud before its state
// was entered got stamped '2023', and the stored value then outranked
// Massachusetts everywhere downstream.

type EditionSource = { code_edition?: string | null; customer_state?: string | null };

/** The edition a project resolves to right now: its stored choice, else its
 * state default. The same resolution every consumer applies. */
export function resolvedEditionKey(project: EditionSource): EditionKey {
  return getEditionPack(project.code_edition ?? defaultEditionForState(project.customer_state))
    .edition;
}

/**
 * What to store when the preparer picks an edition in the selector. Picking
 * the edition the state already defaults to is not an override, so nothing
 * is stored and the project keeps following its state — including a later
 * correction of the state. Any other pick is an explicit choice.
 */
export function editionSelectionToStore(
  choice: EditionKey,
  state?: string | null
): EditionKey | null {
  return choice === defaultEditionForState(state) ? null : choice;
}
